<template>
  <div class="modal-overlay" @click.self="closeModal" role="dialog" aria-modal="true" :aria-labelledby="titleId" :aria-describedby="messageId">
    <div class="modal-content-box" ref="modalContent">
      <button class="close-button" @click="closeModal" aria-label="Close modal">&times;</button>
      <p class="title" :id="titleId">{{ isPasswordRecovery ? 'Password Recovery' : 'Login to PALLAS' }}</p>
      <p class="message" :id="messageId">{{ isPasswordRecovery ? 'Enter your email address to reset your password.' : 'Welcome back! Please enter your credentials to access your PALLAS profile.' }}</p>
      <form @submit.prevent="handleSubmit" class="flex flex-column">

        <div class="form-group">
          <label class="static-label" for="email-input">Email</label>
          <input
            id="email-input"
            :class="['input-field', { 'input-error': errors.email }]"
            type="email"
            name="email"
            v-model="email"
            @input="validateEmail"
            @keydown.enter="handleSubmit"
            autocomplete="email"
            placeholder="Enter your email"
            :aria-invalid="!!errors.email"
            :aria-describedby="errors.email ? 'email-error' : null"
            required
            aria-required="true"
            ref="firstInput"
          />
        </div>

        <div v-if="!isPasswordRecovery" class="form-group password-field">
          <label class="static-label" for="password-input">Password</label>
          <div class="password-input-wrapper">
            <input
              id="password-input"
              :class="['input-field', { 'input-error': errors.password }]"
              :type="showPassword ? 'text' : 'password'"
              name="password"
              v-model="password"
              @input="validatePassword"
              @keydown.enter="handleSubmit"
              autocomplete="current-password"
              placeholder="Enter your password"
              :aria-invalid="!!errors.password"
              :aria-describedby="errors.password ? 'password-error' : 'password-toggle-desc'"
              required
              aria-required="true"
            />
            <button
              type="button"
              class="password-toggle"
              @click="showPassword = !showPassword"
              @keydown.enter="showPassword = !showPassword"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              :aria-pressed="showPassword"
              aria-controls="password-input"
              tabindex="0"
            >
              <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-icon lucide-eye"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off-icon lucide-eye-off"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/></svg>
            </button>
          </div>
        </div>

        <div v-if="errors.email" class="field-error" id="email-error" role="alert" aria-live="polite">{{ errors.email }}</div>
        <div v-if="errors.password && !isPasswordRecovery" class="field-error" id="password-error" role="alert" aria-live="polite">{{ errors.password }}</div>
        <div id="password-toggle-desc" class="visually-hidden">Press to toggle password visibility</div>

        <p v-if="!isPasswordRecovery" class="forgot-password">
          <button type="button" @click="enterPasswordRecovery" @keydown.enter="enterPasswordRecovery" class="link-button" tabindex="0">Forgot Password?</button>
        </p>

        <p v-if="isPasswordRecovery" class="back-to-login">
          <button type="button" @click="exitPasswordRecovery" @keydown.enter="exitPasswordRecovery" class="link-button" tabindex="0">← Back to Login</button>
        </p>

        <button
          v-if="!isLoading && canSubmit"
          @click="handleSubmit"
          @keydown.enter="handleSubmit"
          class="simple-form-button"
          type="submit"
          tabindex="0"
        >
          <span>{{ getSubmitButtonText() }}</span>
        </button>
        <button
          v-else
          class="simple-form-button disabled"
          disabled
          type="button"
          aria-label="Cannot submit - please fill all required fields"
        >
          <span>{{ getSubmitButtonText() }}</span>
        </button>

        <div v-if="errorMessage" class="error-message" role="alert" aria-live="polite" aria-atomic="true">{{ errorMessage }}</div>
        <div v-if="successMessage" class="success-message" role="status" aria-live="polite" aria-atomic="true">{{ successMessage }}</div>

        <p v-if="!isPasswordRecovery" class="signin">
          Don't have an account?
          <button type="button" @click="switchToSignUp" @keydown.enter="switchToSignUp" class="link-button" tabindex="0">
            Sign Up
          </button>
        </p>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, onUnmounted, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { auth } from "../firebase";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { closeModal, openSignUpModal } from '@/composables/useAuthModal';

export default {
  name: "LoginModal",
  setup() {
    const email = ref("");
    const password = ref("");
    const errorMessage = ref("");
    const successMessage = ref("");
    const isLoading = ref(false);
    const isPasswordRecovery = ref(false);
    const showPassword = ref(false);
    const errors = ref({
      email: "",
      password: "",
    });
    const router = useRouter();

    // Modal accessibility
    const modalContent = ref(null);
    const firstInput = ref(null);
    const titleId = `login-title-${Math.random().toString(36).substr(2, 9)}`;
    const messageId = `login-message-${Math.random().toString(36).substr(2, 9)}`;
    const previousFocusElement = ref(null);

    const canSubmit = computed(() => {
      if (isPasswordRecovery.value) {
        return !isLoading.value &&
               email.value.trim() !== '' &&
               !errors.value.email;
      } else {
        return !isLoading.value &&
               email.value.trim() !== '' &&
               password.value !== '' &&
               !errors.value.email &&
               !errors.value.password;
      }
    });

    const validateEmail = () => {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email.value.trim()) {
        errors.value.email = "Email is required.";
      } else if (!emailPattern.test(email.value)) {
        errors.value.email = "Please enter a valid email.";
      } else {
        errors.value.email = "";
      }
    };

    const validatePassword = () => {
      if (!password.value) {
        errors.value.password = "Password is required.";
      } else if (password.value.length < 6) {
        errors.value.password = "Password must be at least 6 characters.";
      } else {
        errors.value.password = "";
      }
    };

    const enterPasswordRecovery = () => {
      isPasswordRecovery.value = true;
      password.value = "";
      errors.value.password = "";
      errorMessage.value = "";
      successMessage.value = "";
    };

    const exitPasswordRecovery = () => {
      isPasswordRecovery.value = false;
      errorMessage.value = "";
      successMessage.value = "";
    };

    const resetPassword = async () => {
      validateEmail();

      if (errors.value.email) {
        errorMessage.value = "Please enter a valid email address.";
        return;
      }

      if (!email.value.trim()) {
        errorMessage.value = "Please enter your email address.";
        return;
      }

      isLoading.value = true;
      errorMessage.value = "";
      successMessage.value = "";

      try {
        await sendPasswordResetEmail(auth, email.value);
        successMessage.value = "If you have a PALLAS account, a password reset email has been sent. Please wait a few minutes and check your spam/junk folder before trying again.";
      } catch (error) {
        console.error("Password reset error:", error);
        switch (error.code) {
          case "auth/invalid-email":
            errorMessage.value = "Invalid email address. Please enter a valid email.";
            break;
          case "auth/user-not-found":
            errorMessage.value = "If you have a PALLAS account, a password reset email has been sent. Please check your email.";
            break;
          case "auth/too-many-requests":
            errorMessage.value = "Too many attempts. Please try again later.";
            break;
          case "auth/network-request-failed":
            errorMessage.value = "Network error. Please check your connection and try again.";
            break;
          default:
            console.error("Password reset error:", error.code, error.message);
            errorMessage.value = "Failed to send reset email. Please try again.";
        }
      } finally {
        isLoading.value = false;
      }
    };

    const getSubmitButtonText = () => {
      if (isLoading.value) {
        return isPasswordRecovery.value ? 'Sending...' : 'Logging in...';
      }
      return isPasswordRecovery.value ? 'Send Reset Email' : 'Submit';
    };

    const handleSubmit = async () => {
      if (isPasswordRecovery.value) {
        await resetPassword();
      } else {
        await login();
      }
    };

    const login = async () => {
      validateEmail();
      if (!isPasswordRecovery.value) {
        validatePassword();
      }

      if (errors.value.email || (!isPasswordRecovery.value && errors.value.password)) {
        return;
      }

      isLoading.value = true;
      errorMessage.value = "";
      successMessage.value = "";

      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email.value,
          password.value
        );

        try {
          const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
          let userData;

          if (userDoc.exists()) {
            userData = userDoc.data();
          } else {
            // Create minimal user document if none exists
            const firstName = userCredential.user.displayName?.split(' ')[0] || '';
            const lastName = userCredential.user.displayName?.split(' ')[1] || '';
            userData = {
              firstName: firstName,
              lastName: lastName,
              email: userCredential.user.email,
              role: "artist",
              verified: false,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };

            try {
              await setDoc(doc(db, "users", userCredential.user.uid), userData);
            } catch (firestoreError) {
              console.error("Failed to create Firestore document:", firestoreError);
            }
          }

            localStorage.setItem(
              "user-info",
              JSON.stringify({
                uid: userCredential.user.uid,
                email: userData.email,
                role: userData.role, 
                adminRole: userData.adminRole,
                profileRole: userData.profileRole,
                verified: userData.verified,
                firstName: userData.firstName,
                lastName: userData.lastName,
              })
            );

          setTimeout(async () => {
            closeModal();
            await nextTick();
            router.push({ name: "ProfileDashboard" });
          }, 1500);
        } catch (firestoreError) {
          console.error("Firestore error during login:", firestoreError);
          errorMessage.value = "Login failed: Unable to load user data. Please try again.";
        }
      } catch (error) {
        console.error("Login error:", error);
        errorMessage.value = mapErrorMessage(error);
      } finally {
        isLoading.value = false;
      }
    };

    const mapErrorMessage = (error) => {
      switch (error.code) {
        case "auth/invalid-credential":
        case "auth/user-not-found":
        case "auth/wrong-password":
          return "Invalid email or password. Please check your credentials.";
        case "auth/invalid-email":
          return "Invalid email address format.";
        case "auth/user-disabled":
          return "This account has been disabled. Please contact support.";
        case "auth/too-many-requests":
          return "Too many failed attempts. Please try again later.";
        case "auth/network-request-failed":
          return "Network error. Please check your connection and try again.";
        case "auth/operation-not-allowed":
          return "Email/password authentication is not enabled.";
        default:
          console.error("Unhandled auth error:", error.code, error.message);
          return "An unexpected error occurred. Please try again.";
      }
    };

    const switchToSignUp = () => {
      closeModal();
      openSignUpModal();
    };

    // Focus management
    const setupFocusManagement = () => {
      previousFocusElement.value = document.activeElement;

      nextTick(() => {
        if (firstInput.value) {
          firstInput.value.focus();
        }
      });
    };

    const trapFocus = (event) => {
      if (!modalContent.value) return;

      const focusableElements = modalContent.value.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      } else {
        trapFocus(event);
      }
    };

    const restoreFocus = () => {
      if (previousFocusElement.value) {
        previousFocusElement.value.focus();
      }
    };

    // Set up focus when component mounts
    onMounted(() => {
      setupFocusManagement();
      window.addEventListener('keydown', handleKeydown);
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown);
      restoreFocus();
    });

    return {
      email,
      password,
      errorMessage,
      successMessage,
      isLoading,
      isPasswordRecovery,
      showPassword,
      errors,
      canSubmit,
      switchToSignUp,
      closeModal,
      validateEmail,
      validatePassword,
      enterPasswordRecovery,
      exitPasswordRecovery,
      getSubmitButtonText,
      handleSubmit,
      modalContent,
      firstInput,
      titleId,
      messageId,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.modal-content-box {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
  max-width: 380px;
  width: 90%;
  padding: var(--paddingSpaceous);
  border-radius: var(--stdRadius);
  position: relative;
  background: var(--bckColor);
  color: var(--textColor);
  box-shadow: var(--stdShadow);
  border: 1px solid var(--borderColor);
  animation: slideUp 0.4s ease;
}

.title {
  font-size: var(--subTitle);
  font-weight: 700;
  text-align: center;
  background: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 4px;
  letter-spacing: 0.02em;
}

.message {
  font-size: var(--mainSubText);
  color: var(--textColor);
  text-align: center;
  line-height: 1.4;
  margin-bottom: 8px;
}

.signin {
  font-size: var(--mainSubText);
  color: var(--textColor);
  text-align: center;
  margin-top: 8px;
}

.signin a {
  color: var(--blendColor);
  font-weight: bold;
  text-decoration: none;
}

.signin a:hover {
  background: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  text-decoration: underline;
}

.flex {
  display: flex;
  width: 100%;
  gap: 6px;
}

.flex-column {
  flex-direction: column;
}

.form-group {
  margin-bottom: var(--paddingStandard);
}

.static-label {
  display: block;
  font-size: var(--labelText);
  font-weight: 600;
  color: var(--textColor);
  margin-bottom: var(--paddingTight);
}

.input-field {
  background: var(--bckColor);
  border: 1px solid var(--borderColor);
  border-radius: var(--stdRadius);
  padding: 11px 14px;
  width: 100%;
  font-size: 14px;
  font-family: var(--font-primary);
  font-weight: 500;
  color: var(--textColor);
  outline: 0;
  transition: all 0.3s ease;
}

.password-input-wrapper {
  position: relative;
}

.input-field:focus {
  outline: none;
  border: 1px solid var(--blendColor);
}

.input-error {
  border: 1px solid var(--errorColor) !important;
}

.form-group:has(.input-error) .static-label {
  color: var(--errorColor) !important;
}

.simple-form-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: var(--bckColor);
  border: 2px solid var(--blendColor);
  border-radius: var(--stdRadius);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  margin-top: var(--paddingTight);
  font-family: var(--font-primary);
  transition: all 0.3s ease;
  color: var(--textColor);
}

.simple-form-button:hover:not(.disabled) {
  transform: translateY(-2px);
  border-color: var(--sciColor);
  box-shadow: 0 4px 12px rgba(101, 102, 190, 0.2);
}

.simple-form-button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--neutralColor);
  border-color: #ccc;
}

.close-button {
  position: absolute;
  top: 15px;
  right: 20px;
  background: none;
  border: none;
  font-size: 28px;
  color: var(--textColor);
  cursor: pointer;
  transition: color 0.3s ease;
}

.close-button:hover {
  color: var(--sciColor);
}

.password-toggle {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--textColor);
  cursor: pointer;
  padding: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle:hover {
  color: var(--sciColor);
}

.password-toggle svg {
  width: 20px;
  height: 20px;
}

.forgot-password {
  margin-top: -var(--paddingTight);
  margin-bottom: var(--paddingTight);
  text-align: right;
  font-size: var(--tagText);
}

.forgot-password a {
  color: var(--blendColor);
  font-weight: bold;
  text-decoration: none;
}

.forgot-password a:hover {
  text-decoration: underline;
}

.back-to-login {
  margin-top: -5px;
  margin-bottom: 10px;
  text-align: left;
  font-size: 14px;
}

.back-to-login a {
  color: var(--blendColor);
  font-weight: bold;
  text-decoration: none;
}

.back-to-login a:hover {
  text-decoration: underline;
}

.field-error {
  color: var(--errorColor);
  font-size: var(--tagText);
  margin-top: -var(--paddingTight);
  margin-bottom: var(--paddingTight);
}

.error-message {
  color: var(--errorColor);
  font-size: var(--labelText);
  text-align: center;
  margin-top: var(--paddingTight);
}

.success-message {
  color: var(--successColor);
  font-size: var(--labelText);
  text-align: center;
  margin-top: var(--paddingTight);
}

@keyframes pulse {
  from {
    transform: scale(0.9);
    opacity: 1;
  }

  to {
    transform: scale(1.8);
    opacity: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Accessibility improvements */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.link-button {
  background: none;
  border: none;
  color: var(--blendColor);
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  margin: 0;
  display: inline;
}

.link-button:hover,
.link-button:focus {
  background: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  text-decoration: underline;
  outline: none;
}

/* Enhanced focus styles */
.input-field:focus {
  outline: 2px solid var(--blendColor);
  outline-offset: 2px;
  border: 1px solid var(--blendColor);
}

.simple-form-button:focus {
  outline: 2px solid var(--sciColor);
  outline-offset: 2px;
}

.close-button:focus {
  outline: 2px solid var(--sciColor);
  outline-offset: 2px;
}

.password-toggle:focus {
  outline: 2px solid var(--blendColor);
  outline-offset: 2px;
}

/* Mobile responsive improvements */
.modal-content-box {
  max-height: 90vh;
  overflow-y: auto;
}

@media (max-width: 480px) {
  .modal-content-box {
    width: 95%;
    padding: 1rem;
    margin: 1rem;
    max-height: 85vh;
  }

  .input-field {
    font-size: 16px; /* Prevents zoom on iOS */
    padding: 12px 14px;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .simple-form-button {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    min-height: 44px; /* Touch target size */
  }

  .close-button {
    font-size: 24px;
    top: 12px;
    right: 16px;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .password-input-container .toggle-password {
    min-width: 44px;
    min-height: 44px;
  }
}
</style>